import { useState } from 'react';
import { Box, Button, DialogActions, Typography, styled } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { COLORS } from '../../../utils/Application_Constants';
import usePostNewTimesheetVersion from '../../hooks/usePostNewTimesheetVersion';

const Textarea = styled('textarea')({
    width: '100%',
    height: '100px',
    resize: 'none',
    border: 'none',
    borderBottom: `1px solid ${COLORS.LT_MIDNIGHT10}`,
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: '1rem',
    padding: '8px 0',
    overflow: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
});

function CorrectionForm({ onSuccess, id, onClose, onError }) {
    const [newVersionReason, setNewVersionReason] = useState('');
    const queryClient = useQueryClient();

    const { mutate: postNewTimesheetVersion, isLoading: isPostLoading } =
        usePostNewTimesheetVersion({
            onSuccess: (data) => {
                const newTimesheetId = data?.id;

                queryClient.invalidateQueries({ queryKey: ['processedTimesheets'] });
                queryClient.invalidateQueries({
                    queryKey: [
                        'processedTimesheetDetails',
                        { processedTimesheetId: id.toString() },
                    ],
                });
                queryClient.invalidateQueries({
                    queryKey: ['getActivityFeed', { processedTimesheetId: id.toString() }],
                });

                // Pass the new ID to the parent and close the modal
                if (onSuccess) onSuccess(newTimesheetId);
            },
            onError: (error) => {
                console.error('Error creating new processed timesheet version:', error);
                onError('There was an error submitting your request. Please try again.');
            },
        });

    const handleSubmit = () => {
        if (id == null) {
            onError('There was an error submitting your request. Please try again.');
            return;
        }

        postNewTimesheetVersion({
            processedTimesheetId: id,
            formattedCommentContent: newVersionReason,
        });
    };

    return (
        <Box display="flex" flexDirection="column">
            <Typography variant="h5" sx={{ mb: 3 }}>
                Create a new version
            </Typography>
            <Typography variant="body2" sx={{ float: 'left', mb: 2 }}>
                You are about to create a new version of this Processed Timesheet for manual
                editing. The original processed timesheet will remain unchanged, but its status will
                be updated to 'Outdated.'
            </Typography>
            <Typography
                sx={{
                    color: COLORS.BLACK,
                    fontSize: '16px',
                    fontWeight: '600',
                }}
                mb={2}
            >
                Reason for new processed timesheet
            </Typography>
            <Box mb={1}>
                <Textarea
                    placeholder="Add a comment (optional)..."
                    value={newVersionReason}
                    onChange={(e) => setNewVersionReason(e.target.value)}
                    maxLength={800}
                />
                <Box display="flex" justifyContent="flex-start">
                    <Typography variant="caption" color={COLORS.LT_MI}>
                        {newVersionReason.length}/800
                    </Typography>
                </Box>
            </Box>
            <DialogActions sx={{ justifyContent: 'flex-end', padding: 0 }}>
                <Button
                    disabled={isPostLoading}
                    color="error"
                    onClick={onClose}
                    sx={{ p: '8px 10px', height: '33px' }}
                >
                    Cancel
                </Button>
                <Button
                    loading={isPostLoading}
                    variant="contained"
                    color="primary"
                    sx={{
                        borderRadius: ({ shape }) => `${shape.borderRadiusVariants.md}px`,
                        p: '8px 10px',
                        width: '183px',
                        height: '33px',
                    }}
                    onClick={handleSubmit}
                >
                    Create New Version
                </Button>
            </DialogActions>
        </Box>
    );
}

export default CorrectionForm;
